import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  VideoCamera, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { formatCurrency, formatTimeShort, formatDateShort } from "@/lib/format";
import { DateRangePicker, type DateRangeValue } from "@/components/admin/DateRangePicker";
import { TherapistKpiBand } from "@/components/therapist/dashboard/TherapistKpiBand";
import {
  TherapistActionInbox,
  type TherapistActionItem,
} from "@/components/therapist/dashboard/TherapistActionInbox";

export const Route = createFileRoute("/dashboard/therapist/")({
  component: TherapistDashboard,
});

interface Upcoming {
  id: string;
  scheduledAt: string;
  durationMinutes: number;
  modality: string;
  meetLink: string | null;
  status: string;
  clientName: string;
  serviceName: string | null;
}
interface DashboardData {
  firstName: string;
  status: string;
  completeness: { hasBio: boolean; hasSpecializations: boolean; hasServices: boolean; hasAvailability: boolean; hasVideo: boolean };
  upcoming: Upcoming[];
  reviews: { avgRating: number | null; reviewCount: number };
}
interface StatsData {
  from: string;
  to: string;
  earnings: number;
  earningsChange: number | null;
  completedSessions: number;
  completedChange: number | null;
  pendingPayout: number;
  pendingPayoutSessionCount: number;
  avgRating: number | null;
  reviewCount: number;
  actionItems: TherapistActionItem[];
}

const teal = "oklch(37.12% 0.059 186.62)";

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultRange(): DateRangeValue {
  const today = formatLocalDate(new Date());
  return { from: today, to: today };
}

function minutesUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60000);
}

function TherapistDashboard() {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateRangeValue>(getDefaultRange);

  const { data, isLoading } = useQuery({
    queryKey: ["therapist", "dashboard"],
    queryFn: async () => {
      const res = await fetch("/api/therapist/dashboard");
      await throwIfNotOk(res, t("therapist.dashboard.errorLoading"));
      return res.json() as Promise<DashboardData>;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ["therapist", "stats", dateRange.from, dateRange.to],
    queryFn: async () => {
      const res = await fetch(`/api/therapist/stats?from=${dateRange.from}&to=${dateRange.to}`);
      await throwIfNotOk(res, t("therapist.dashboard.errorLoading"));
      return res.json() as Promise<StatsData>;
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="h-10 w-64 animate-pulse rounded-lg bg-card" />
        <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl border bg-card" />)}
        </div>
        <div className="h-48 animate-pulse rounded-2xl border bg-card" />
        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div className="h-52 animate-pulse rounded-2xl border bg-card" />
          <div className="h-52 animate-pulse rounded-xl border bg-card" />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const modalityLabel = (m: string) => (m === "in_person" ? "Uživo" : "Online");
  const next = data.upcoming[0];
  const nextMin = next ? minutesUntil(next.scheduledAt) : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl" style={{ fontFamily: "var(--font-heading)" }}>
            {t("therapist.dashboard.greeting", { name: data.firstName })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("therapist.dashboard.sessionsToday", { count: data.upcoming.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={cn("inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
            data.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
            <CheckCircle className="size-3.5" weight="fill" />
            {data.status === "approved" ? t("therapist.dashboard.verified") : t("therapist.dashboard.pendingStatus")}
          </span>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>
      </div>

      {/* KPI band */}
      {stats && (
        <TherapistKpiBand
          earnings={stats.earnings}
          earningsChange={stats.earningsChange}
          completedSessions={stats.completedSessions}
          completedChange={stats.completedChange}
          pendingPayout={stats.pendingPayout}
          pendingPayoutSessionCount={stats.pendingPayoutSessionCount}
          avgRating={stats.avgRating}
          reviewCount={stats.reviewCount}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Action center — what needs attention right now */}
      {stats && <TherapistActionInbox items={stats.actionItems} />}

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Next session hero */}
        <div className="overflow-hidden rounded-2xl border border-border/60 text-white shadow-sm"
          style={{ background: "linear-gradient(135deg, oklch(42.03% 0.0663 186.62), oklch(27.52% 0.0467 186.62))" }}>
          {next ? (
            <div className="px-7 py-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/60">
                {t("therapist.dashboard.nextSession")}
                {nextMin != null && nextMin >= 0 && nextMin <= 180 ? ` · ${t("therapist.dashboard.inMinutes", { min: nextMin })}` : ""}
              </p>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="text-5xl font-bold tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{formatTimeShort(next.scheduledAt)}</span>
                <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium">{modalityLabel(next.modality)}</span>
              </div>
              <p className="mt-3 text-lg font-medium">{next.clientName}</p>
              <p className="text-sm text-white/70">{next.serviceName ?? "—"} · {formatDateShort(next.scheduledAt)}</p>
              {next.meetLink && (
                <a href={next.meetLink} target="_blank" rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold shadow" style={{ color: teal }}>
                  <VideoCamera className="size-4" weight="fill" /> {t("therapist.dashboard.join")}
                </a>
              )}
            </div>
          ) : (
            <div className="flex h-full min-h-44 flex-col items-center justify-center px-7 py-10 text-center">
              <p className="text-sm text-white/70">{t("therapist.dashboard.noUpcoming")}</p>
            </div>
          )}
        </div>

        {/* Upcoming list */}
        <div className="rounded-xl border border-border/50 bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border/40 px-5 py-3.5">
            <h2 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t("therapist.dashboard.upcoming")}</h2>
            <Link to="/dashboard/therapist/calendar" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              {t("sidebar.calendar")} <ArrowRight className="size-3" />
            </Link>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t("therapist.dashboard.noUpcoming")}</p>
          ) : (
            <ol className="divide-y divide-border/30">
              {data.upcoming.map((s, i) => (
                <li key={s.id} className="flex items-center gap-4 px-5 py-3.5">
                  <span className="w-14 shrink-0 text-sm font-bold tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{formatTimeShort(s.scheduledAt)}</span>
                  <span className={cn("size-2.5 shrink-0 rounded-full", i === 0 ? "bg-emerald-500" : "bg-border")} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{s.clientName}</p>
                    <p className="truncate text-xs text-muted-foreground">{(s.serviceName ?? "—")} · {modalityLabel(s.modality)} · {formatDateShort(s.scheduledAt)}</p>
                  </div>
                  {i === 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{t("therapist.dashboard.next")}</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
